/**
 * Navigation model — the §14.4 Admin Panel destinations, grouped the way an
 * operations team thinks: overview → live ops → directory → money → growth →
 * system. Single source for the sidebar, the topbar title/breadcrumbs and
 * route wiring; later-milestone screens slot in by replacing the lazy import
 * target only.
 *
 * RBAC (§11.1): each destination carries the `view` permission key of its
 * contract surface — the sidebar hides what the actor cannot see; the server
 * stays the authority. `permission: null` = visible to every authenticated
 * admin (dashboard only).
 */

import {
  Activity,
  Bell,
  Bike,
  ClipboardList,
  Flag,
  GalleryHorizontal,
  LayoutGrid,
  Inbox,
  FileLock2,
  LayoutDashboard,
  LayoutPanelTop,
  LayoutTemplate,
  Map,
  PictureInPicture2,
  MapPinned,
  Megaphone,
  PackageX,
  Percent,
  Route,
  Scale,
  ScrollText,
  Settings,
  ShieldAlert,
  ShieldCheck,
  SlidersHorizontal,
  Store,
  Tags,
  Truck,
  Ticket,
  Users,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import { CUSTOMER_ADS_FLAG } from "../data/useFeatureFlag";
import type { MessageKey } from "../i18n/messages";

export interface NavItem {
  /** Route path segment ("" = index / dashboard). */
  readonly path: string;
  readonly labelKey: MessageKey;
  readonly titleKey: MessageKey;
  readonly icon: LucideIcon;
  /** Contract permission key gating visibility; null = always visible. */
  readonly permission: string | null;
  /**
   * Effective feature flag the destination ALSO needs (M7-106). Off, loading
   * or unreadable ⇒ the item is absent, never greyed (the M7-96 posture).
   */
  readonly flag?: string;
}

export interface NavGroup {
  readonly labelKey: MessageKey;
  readonly items: ReadonlyArray<NavItem>;
}

export const NAV_GROUPS: ReadonlyArray<NavGroup> = [
  {
    labelKey: "nav.group.overview",
    items: [
      {
        path: "",
        labelKey: "nav.dashboard",
        titleKey: "screen.dashboard.title",
        icon: LayoutDashboard,
        permission: null,
      },
    ],
  },
  {
    labelKey: "nav.group.operations",
    items: [
      {
        path: "orders",
        labelKey: "nav.orders",
        titleKey: "screen.orders.title",
        icon: ClipboardList,
        permission: "orders.order.view",
      },
      {
        path: "dispatch",
        labelKey: "nav.dispatchMap",
        titleKey: "screen.dispatchMap.title",
        icon: Map,
        permission: "dispatch.assign.view",
      },
      {
        // M7-07 — the assignment audit trail lives beside the live map: it
        // answers "who had this order" once the map has moved on.
        path: "assignments",
        labelKey: "nav.assignments",
        titleKey: "assignments.title",
        icon: Route,
        permission: "dispatch.assign.view",
      },
      {
        // M7-74 — the orders dispatch could not place. Directly under the map:
        // it is the same shift's work, and nothing else on the panel says an
        // order found no driver.
        path: "unassigned-orders",
        labelKey: "nav.unassignedOrders",
        titleKey: "unassigned.title",
        icon: PackageX,
        permission: "orders.order.view",
      },
      {
        // M6-19 — the §6.6 escalation queue: a driver is blocked and waiting,
        // so it belongs in Operations next to the map, not buried in settings.
        path: "geofence-overrides",
        labelKey: "nav.geofenceOverrides",
        titleKey: "screen.geofenceOverrides.title",
        icon: ShieldAlert,
        permission: "dispatch.override.decide",
      },
      {
        // M7-07 — §13 config-driven dispatch strategy selection + tuning.
        path: "dispatch-rules",
        labelKey: "nav.dispatchRules",
        titleKey: "dispatchRules.title",
        icon: SlidersHorizontal,
        permission: "dispatch.rules.manage",
      },
      {
        path: "zones",
        labelKey: "nav.zones",
        titleKey: "screen.zones.title",
        icon: MapPinned,
        permission: "geo.zone.view",
      },
    ],
  },
  {
    labelKey: "nav.group.directory",
    items: [
      {
        path: "merchants",
        labelKey: "nav.merchants",
        titleKey: "screen.merchants.title",
        icon: Store,
        permission: "merchants.merchant.view",
      },
      {
        // M7-148 — the owner-managed store categories (owner ruling
        // 2026-09-15). Directly under Merchants: it is the vocabulary the
        // merchant form and the customer app's filter chips both read.
        path: "store-categories",
        labelKey: "nav.storeCategories",
        titleKey: "screen.storeCategories.title",
        icon: Tags,
        permission: "merchants.category.view",
      },
      {
        // M7-43b — the §14.5 join-flow review queue lives beside Merchants;
        // its sidebar entry carries the PENDING count badge (merchants.yaml
        // v1.4.0, permission code-seeded by M7-42).
        path: "merchant-applications",
        labelKey: "nav.merchantApplications",
        titleKey: "applications.title",
        icon: Inbox,
        permission: "merchants.application.view",
      },
      {
        path: "drivers",
        labelKey: "nav.drivers",
        titleKey: "screen.drivers.title",
        icon: Bike,
        permission: "drivers.driver.view",
      },
      {
        // M7-150 — the owner-managed vehicle types (owner ruling 2026-09-15).
        // Directly under Drivers: it is the vocabulary the driver vehicle
        // form, the merchant store form and dispatch matching all read.
        path: "vehicle-types",
        labelKey: "nav.vehicleTypes",
        titleKey: "screen.vehicleTypes.title",
        icon: Truck,
        permission: "drivers.vehicle_type.view",
      },
      {
        path: "customers",
        labelKey: "nav.customers",
        titleKey: "screen.customers.title",
        icon: Users,
        permission: "customers.customer.view",
      },
    ],
  },
  {
    labelKey: "nav.group.finance",
    items: [
      {
        path: "wallets",
        labelKey: "nav.wallets",
        titleKey: "screen.wallets.title",
        icon: Wallet,
        permission: "wallet.wallet.view",
      },
      {
        path: "settlement",
        labelKey: "nav.settlement",
        titleKey: "screen.settlement.title",
        icon: Scale,
        permission: "settlement.record.view",
      },
      {
        path: "subscriptions",
        labelKey: "nav.subscriptions",
        titleKey: "screen.subscriptions.title",
        icon: Ticket,
        permission: "subscriptions.subscription.view",
      },
    ],
  },
  {
    labelKey: "nav.group.growth",
    items: [
      {
        path: "promotions",
        labelKey: "nav.promotions",
        titleKey: "screen.promotions.title",
        icon: Percent,
        permission: "promotions.promotion.view",
      },
      {
        // M7-106 — Wave 14 customer-app ad banners, beside Promotions: the
        // owner sells placement the same way they sell promotions.
        path: "ads",
        labelKey: "nav.ads",
        titleKey: "screen.ads.title",
        icon: Megaphone,
        permission: "marketing.banner.view",
        flag: CUSTOMER_ADS_FLAG,
      },
      {
        // M7-182 — Wave 22 customer home sections («توصيل مجاني» / «خصومات»):
        // show/hide, titles, artwork. Marketing surface on the banner keys,
        // NOT a flagged feature — the sections exist whether ads are on.
        path: "home-sections",
        labelKey: "nav.homeSections",
        titleKey: "screen.homeSections.title",
        icon: LayoutTemplate,
        permission: "marketing.banner.view",
      },
      {
        // M7-253 — store collections: the portrait-tile row on the customer
        // home. Directly under «أقسام الرئيسية»: the same home screen, the
        // same banner keys, the same operator.
        path: "collections",
        labelKey: "nav.collections",
        titleKey: "screen.collections.title",
        icon: GalleryHorizontal,
        permission: "marketing.banner.view",
      },
      {
        // M7-266 — per-category layout (subcategory tiles, sections, banners
        // — owner ruling 2026-10-05), beside «المجموعات»: the same marketing
        // surface on the same banner keys.
        path: "category-layout",
        labelKey: "nav.categoryLayout",
        titleKey: "screen.categoryLayout.title",
        icon: LayoutGrid,
        permission: "marketing.banner.view",
      },
      {
        // M7-269 — the customer home's admin-arranged middle block (owner
        // ruling 2026-10-05): order of the built-ins, image / title sections,
        // rotating banner groups. Beside «تخطيط الأقسام», same keys.
        path: "home-layout",
        labelKey: "nav.homeLayout",
        titleKey: "screen.homeLayout.title",
        icon: LayoutPanelTop,
        permission: "marketing.banner.view",
      },
      {
        // M7-301 — the customer app's image pop-ups (owner request
        // 2026-10-24: several, each with a per-day frequency). Beside the
        // home layout: the same app surface, the same operator. ONE key,
        // `marketing.popup.manage`, both sees and changes (the contract).
        path: "popups",
        labelKey: "nav.popups",
        titleKey: "screen.popups.title",
        icon: PictureInPicture2,
        permission: "marketing.popup.manage",
      },
      {
        // M7-38 — §7.2 cross-merchant engagement analytics: the renewal
        // sales argument lives beside promotions in Growth.
        path: "engagement",
        labelKey: "nav.engagement",
        titleKey: "engagement.title",
        icon: Activity,
        permission: "merchants.engagement.view_all",
      },
    ],
  },
  {
    labelKey: "nav.group.system",
    items: [
      {
        path: "notifications",
        labelKey: "nav.notifications",
        titleKey: "screen.notifications.title",
        icon: Bell,
        permission: "notifications.template.view",
      },
      {
        path: "feature-flags",
        labelKey: "nav.featureFlags",
        titleKey: "screen.featureFlags.title",
        icon: Flag,
        permission: "settings.flag.view",
      },
      {
        path: "settings",
        labelKey: "nav.settings",
        titleKey: "screen.settings.title",
        icon: Settings,
        permission: "settings.setting.view",
      },
      {
        // M7-309 — the privacy policy the apps and stores link to (owner
        // 2026-10-25), beside Settings: platform configuration, one key.
        path: "privacy-policy",
        labelKey: "nav.privacyPolicy",
        titleKey: "screen.privacyPolicy.title",
        icon: FileLock2,
        permission: "platform.legal.manage",
      },
      {
        // M7-23 — RBAC management: the platform's own access control was
        // contract-complete and UI-less until now.
        path: "roles",
        labelKey: "nav.roles",
        titleKey: "roles.title",
        icon: ShieldCheck,
        permission: "identity.role.view",
      },
      {
        path: "audit",
        labelKey: "nav.audit",
        titleKey: "screen.audit.title",
        icon: ScrollText,
        // audit.yaml v1.0.0 (M6-18) — the read surface is contracted now.
        permission: "identity.audit.view",
      },
    ],
  },
];

export const NAV_ITEMS: ReadonlyArray<NavItem> = NAV_GROUPS.flatMap((group) => group.items);

/** Finds the active nav item for the topbar title / breadcrumb root. */
export function navItemForPath(pathname: string): NavItem | undefined {
  const segment = pathname.replace(/^\/+|\/+$/g, "").split("/")[0] ?? "";
  return NAV_ITEMS.find((item) => item.path === segment);
}
